import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { authService } from '../../services/authService';

import type { AdminBreadcrumbSegment } from '../../components/admin/AdminBreadcrumb';
import AdminLayout from '../../components/admin/AdminLayout';
import FormateurChangePasswordPanel from '../../components/formateur/FormateurChangePasswordPanel';
import FormateurDashboardPanel from '../../components/formateur/FormateurDashboardPanel';
import FormateurPageShell from '../../components/formateur/FormateurPageShell';
import FormateurProfilPanel from '../../components/formateur/FormateurProfilPanel';
import FormateurRemunerationPanel from '../../components/formateur/FormateurRemunerationPanel';
import ApprenantParrainagePanel from '../../components/apprenant/ApprenantParrainagePanel';
import PortalWalletPanel from '../../components/shared/PortalWalletPanel';
import FormateurSessionsPanel from '../../components/formateur/FormateurSessionsPanel';
import FormateurEvaluationsPanel from '../../components/formateur/FormateurEvaluationsPanel';
import FormateurElearningPanel from '../../components/formateur/FormateurElearningPanel';
import PortalNotificationsPanel from '../../components/shared/PortalNotificationsPanel';
import PortalHelpPanel from '../../components/shared/PortalHelpPanel';
import { PORTAL_HELP_PATHS } from '../../config/portalHelpContent';
import type { AdminNavItem } from '../../types/admin';
import { fetchFormateurAcademicPerformance } from '../../services/academiaFormateurService';

const NAV: AdminNavItem[] = [
  { id: 'dashboard', label: 'Accueil', icon: 'dashboard', path: '/formateur' },
  { id: 'sessions', label: 'Mes sessions', icon: 'event', path: '/formateur/sessions' },
  { id: 'evaluations', label: 'Évaluations', icon: 'quiz', path: '/formateur/evaluations' },
  { id: 'elearning', label: 'E-learning', icon: 'play_circle', path: '/formateur/elearning' },
  { id: 'profil', label: 'Mon profil', icon: 'person', path: '/formateur/profil' },
  { id: 'remuneration', label: 'Rémunération', icon: 'payments', path: '/formateur/remuneration' },
  { id: 'parrainage', label: 'Parrainage', icon: 'groups', path: '/formateur/parrainage' },
  { id: 'wallet', label: 'Portefeuille', icon: 'account_balance_wallet', path: '/formateur/wallet' },
];

const BREADCRUMB_LABELS: Record<string, string> = {
  dashboard: 'Accueil',
  sessions: 'Mes sessions',
  evaluations: 'Évaluations',
  elearning: 'E-learning',
  profil: 'Mon profil',
  password: 'Mot de passe',
  remuneration: 'Rémunération',
  parrainage: 'Parrainage',
  wallet: 'Mon portefeuille',
  performance: 'Impact académique',
  aide: 'Centre d’aide',
  notifications: 'Notifications',
};

interface FormateurAcademiaPageProps {
  user: {
    telephone?: string;
    mustCompleteProfile?: boolean;
    mustChangePassword?: boolean;
    profil?: { prenom?: string; nom?: string; photoUrl?: string | null };
  };
  onLogout: () => void;
  onUserUpdated?: (user: FormateurAcademiaPageProps['user']) => void;
}

function isFormateurProfilPath(pathname: string) {
  return pathname === '/formateur/profil' || pathname.startsWith('/formateur/profil/');
}

function isFormateurPasswordPath(pathname: string) {
  return (
    pathname === '/formateur/changer-mot-de-passe' ||
    pathname.startsWith('/formateur/changer-mot-de-passe/')
  );
}

function isFormateurHelpPath(pathname: string) {
  return pathname === '/formateur/aide' || pathname.startsWith('/formateur/aide/');
}

function FormateurOnboardingGuard({
  user,
  children,
}: {
  user: FormateurAcademiaPageProps['user'];
  children: ReactNode;
}) {
  const location = useLocation();

  if (user.mustCompleteProfile && !isFormateurProfilPath(location.pathname) && !isFormateurHelpPath(location.pathname)) {
    return <Navigate to="/formateur/profil" replace />;
  }
  if (
    user.mustChangePassword &&
    !user.mustCompleteProfile &&
    !isFormateurPasswordPath(location.pathname) &&
    !isFormateurProfilPath(location.pathname) &&
    !isFormateurHelpPath(location.pathname)
  ) {
    return <Navigate to="/formateur/profil" replace />;
  }
  return <>{children}</>;
}

type FormateurPortalUser = FormateurAcademiaPageProps['user'];

function formateurDisplayName(user: FormateurPortalUser) {
  return [user.profil?.prenom, user.profil?.nom].filter(Boolean).join(' ') || user.telephone || 'Formateur';
}

export default function FormateurAcademiaPage({ user, onLogout, onUserUpdated }: FormateurAcademiaPageProps) {
  const location = useLocation();
  const fullName = [user.profil?.prenom, user.profil?.nom].filter(Boolean).join(' ');

  const viewKey = useMemo(() => {
    if (isFormateurPasswordPath(location.pathname)) return 'password';
    if (isFormateurProfilPath(location.pathname)) return 'profil';
    if (location.pathname.startsWith('/formateur/remuneration')) return 'remuneration';
    if (location.pathname.startsWith('/formateur/parrainage')) return 'parrainage';
    if (location.pathname.startsWith('/formateur/wallet')) return 'wallet';
    if (location.pathname.startsWith('/formateur/notifications')) return 'notifications';
    if (location.pathname.startsWith('/formateur/performance')) return 'performance';
    if (location.pathname.startsWith('/formateur/sessions')) return 'sessions';
    if (location.pathname.startsWith('/formateur/evaluations')) return 'evaluations';
    if (location.pathname.startsWith('/formateur/elearning')) return 'elearning';
    if (location.pathname.startsWith('/formateur/aide')) return 'aide';
    return 'dashboard';
  }, [location.pathname]);

  const breadcrumb: AdminBreadcrumbSegment[] = useMemo(
    () => [
      { label: 'Espace formateur' },
      { label: BREADCRUMB_LABELS[viewKey] ?? 'Accueil' },
    ],
    [viewKey],
  );

  const refreshUser = async () => {
    const profile = await authService.getCurrentUser();
    if (profile && onUserUpdated) onUserUpdated(profile);
  };

  return (
    <FormateurOnboardingGuard user={user}>
    <AdminLayout
      navigation={NAV}
      user={{
        name: fullName || user.telephone || 'Formateur',
        subtitle: 'Portail formateur',
        photoUrl: user.profil?.photoUrl,
        prenom: user.profil?.prenom,
        nom: user.profil?.nom,
      }}
      onLogout={onLogout}
      searchQuery=""
      onSearchChange={() => {}}
      breadcrumb={breadcrumb}
      brandAlt="JCONSULT MY Academia"
      helpPath={PORTAL_HELP_PATHS.formateur}
    >
      <Routes>
        <Route index element={<FormateurDashboardPanel user={user} />} />
        <Route path="sessions" element={<FormateurSessionsPanel user={user} />} />
        <Route path="evaluations" element={<FormateurEvaluationsPanel />} />
        <Route path="elearning" element={<FormateurElearningPanel />} />
        <Route
          path="profil"
          element={
            <FormateurProfilPanel
              user={user}
              onboarding={Boolean(user.mustCompleteProfile)}
              mustChangePassword={Boolean(user.mustChangePassword)}
              onProfileSaved={refreshUser}
              onPasswordChanged={refreshUser}
            />
          }
        />
        <Route
          path="changer-mot-de-passe"
          element={
            <FormateurChangePasswordPanel
              user={user}
              onboarding={Boolean(user.mustChangePassword)}
              onPasswordChanged={refreshUser}
            />
          }
        />
        <Route path="remuneration" element={<FormateurRemunerationPanel user={user} />} />
        <Route path="parrainage" element={<ApprenantParrainagePanel portal="formateur" user={user} />} />
        <Route
          path="wallet"
          element={<PortalWalletPanel portal="formateur" parrainagePath="/formateur/parrainage" />}
        />
        <Route path="performance" element={<FormateurPerformancePanel user={user} />} />
        <Route path="aide" element={<PortalHelpPanel portal="formateur" />} />
        <Route path="notifications" element={<PortalNotificationsPanel portal="formateur" />} />
        <Route path="*" element={<Navigate to="/formateur" replace />} />
      </Routes>
    </AdminLayout>
    </FormateurOnboardingGuard>
  );
}

function FormateurPerformancePanel({ user }: { user: FormateurPortalUser }) {
  const [data, setData] = useState<{
    cohortes?: {
      code: string;
      libelle?: string;
      filiere?: string;
      moyenneCohorteSur20: number | null;
      tauxPresenceMoyenPct: number | null;
      nombreApprenants: number;
      sessionsAnimees: number;
    }[];
    synthese?: { nombreCohortes?: number; moyenneGlobaleSur20: number | null };
  } | null>(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setError('');
    setIsLoading(true);
    try {
      setData(await fetchFormateurAcademicPerformance());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur chargement');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const cohortes = data?.cohortes ?? [];
  const moyenneGlobale = data?.synthese?.moyenneGlobaleSur20;

  return (
    <FormateurPageShell
      title="Impact académique"
      description="Synthèse des performances des cohortes que vous animez : moyennes sur 20 et taux de présence."
      isLoading={isLoading}
      isError={Boolean(error)}
      errorMessage={error}
      onRetry={() => void load()}
      sideCard={{
        label: 'Moyenne globale',
        value: moyenneGlobale != null ? `${moyenneGlobale}/20` : '—',
        helper: `${data?.synthese?.nombreCohortes ?? cohortes.length} cohorte(s) · ${formateurDisplayName(user)}`,
        sideLinks: [
          { label: 'Mes sessions', to: '/formateur/sessions', icon: 'event' },
          { label: 'Rémunération', to: '/formateur/remuneration', icon: 'payments' },
        ],
      }}
    >
      {moyenneGlobale != null ? (
        <div className="w-full rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
          <p className="text-xs font-bold uppercase text-on-surface-variant">Vue d&apos;ensemble</p>
          <p className="mt-1 text-3xl font-black text-primary">{moyenneGlobale}/20</p>
          <p className="mt-1 text-sm text-on-surface-variant">
            {data?.synthese?.nombreCohortes ?? cohortes.length} cohorte(s) suivie(s)
          </p>
        </div>
      ) : null}
      {cohortes.length === 0 && !isLoading ? (
        <p className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest px-4 py-6 text-sm text-on-surface-variant">
          Aucune cohorte liée à vos sessions pour le moment.
        </p>
      ) : (
        <ul className="w-full space-y-3">
          {cohortes.map((c) => (
            <li
              key={c.code}
              className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest px-4 py-3 text-sm shadow-sm"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-bold">
                  {c.code}
                  {c.libelle ? ` — ${c.libelle}` : ''}
                </span>
                <span className="font-black text-primary">
                  {c.moyenneCohorteSur20 != null ? `${c.moyenneCohorteSur20}/20` : '—'}
                </span>
              </div>
              <p className="mt-1 text-xs text-on-surface-variant">
                {c.filiere ? `${c.filiere} · ` : ''}
                {c.nombreApprenants} apprenant(s) · {c.sessionsAnimees} session(s)
                {c.tauxPresenceMoyenPct != null ? ` · ${c.tauxPresenceMoyenPct}% présence` : ''}
              </p>
            </li>
          ))}
        </ul>
      )}
    </FormateurPageShell>
  );
}
